import React, { useEffect, useRef } from "react";

const toc = [
  ["Rendering Approaches", "rendering-approaches"],
  ["Rays", "rays"],
  ["Ray Tracing Algorithm", "algorithm"],
  ["Camera Model", "camera-model"],
  ["Ray Generation", "ray-generation"],
  ["FOV & Aspect Ratio", "fov-aspect"],
  ["Ray–Sphere Intersection", "ray-sphere"],
  ["Ray–Plane Intersection", "ray-plane"],
  ["Ray–Triangle Intersection", "ray-triangle"],
  ["Point-in-Triangle Test", "point-in-triangle"],
  ["AABB Overview", "aabb-overview"],
  ["AABB Algorithm", "aabb-algorithm"],
  ["AABB Routines", "aabb-routines"],
];

const F = ({ children }) => (
  <div className="formula-block">
    {(Array.isArray(children) ? children : [children]).map((line, i) => (
      <div key={i} className="formula-row">{line}</div>
    ))}
  </div>
);

const RaytracingIntro = ({ renderToc }) => {
  const ref = useRef(null);

  useEffect(() => {
    const tryRender = () => {
      if (window.renderMathInElement && ref.current) {
        window.renderMathInElement(ref.current, {
          delimiters: [
            { left: "$$", right: "$$", display: true },
            { left: "$", right: "$", display: false },
          ],
          throwOnError: false,
        });
      } else {
        setTimeout(tryRender, 100);
      }
    };
    tryRender();
  }, []);

  useEffect(() => {
    if (renderToc) renderToc(toc);
  }, [renderToc]);

  return (
    <div ref={ref}>
      <h1>Introduction to Ray Tracing</h1>
      <p className="subtitle">by Jeff Wilson, Ph.D.</p>

      {/* ── RENDERING APPROACHES ── */}
      <h2 id="rendering-approaches">Rendering Approaches</h2>
      <p>Two fundamental strategies for synthesising 3D scenes:</p>
      <ul>
        <li>
          <strong>Object-Order Rendering</strong> — iterate over each object
          and determine its contribution to the image (e.g. rasterisation).
        </li>
        <li>
          <strong>Image-Order Rendering</strong> — iterate over each pixel and
          find which scene objects contribute to it (e.g. ray tracing).
        </li>
      </ul>

      {/* ── RAYS ── */}
      <h2 id="rays">Rays</h2>
      <p>
        A ray is a <strong>situated vector</strong>: an origin point and a
        direction. Because it starts at a specific location, both must be
        preserved.
      </p>
      <ul>
        <li>Also called: Viewing Ray, Primary Ray, Camera Ray</li>
        <li>Cast from the camera through each pixel into the scene</li>
      </ul>

      {/* ── ALGORITHM ── */}
      <h2 id="algorithm">Ray Tracing Algorithm</h2>
      <ol>
        <li>
          <strong>Ray Generation</strong> — compute a viewing ray per pixel
        </li>
        <li>
          <strong>Ray Intersection</strong> — find the closest hit; record
          position and surface normal
        </li>
        <li>
          <strong>Shading</strong> — compute pixel colour from the hit point,
          lights, and surface properties
        </li>
      </ol>
      <pre>{`for each pixel do\n    compute viewing ray\n    find first object hit by ray and its surface normal n\n    set pixel color = f(hit_point, light, n)`}</pre>

      {/* ── CAMERA MODEL ── */}
      <h2 id="camera-model">Camera Model</h2>
      <p>
        A physical pinhole camera places the aperture between the sensor and the
        scene, producing an inverted image. The virtual camera simplifies this:
        rays travel directly from the scene to the view plane as if pixels
        sample light from directions defined by a focal point behind the sensor.
      </p>

      {/* ── RAY GENERATION ── */}
      <h2 id="ray-generation">Ray Generation</h2>
      <p>
        Define a <strong>camera frame</strong> at eye {"$E$"}, looking down{" "}
        {"$-\\mathbf{w}$"}. The view plane is at distance {"$d$"}, spanning{" "}
        {"$[l,r]$"} horizontally and {"$[b,t]$"} vertically, containing{" "}
        {"$n_x\\times n_y$"} pixels.
      </p>

      <h3>Pixel Centre on View Plane</h3>
      <p>Rays are centred in pixels (0.5 offset):</p>
      <F>
        {[
          "$$u_i = l + \\frac{(r-l)(i+0.5)}{n_x}$$",
          "$$v_j = b + \\frac{(t-b)(j+0.5)}{n_y}$$",
          "$$\\mathbf{S}_{i,j} = E - d\\,\\mathbf{w} + u_i\\,\\mathbf{u} + v_j\\,\\mathbf{v}$$",
        ]}
      </F>

      <h3>Parametric Ray Equation</h3>
      <F>
        {[
          "$$P(t) = E + t(S - E)$$",
          "$t=0$: eye $E$ — $t=1$: screen $S$ — $t>1$: extrapolated into scene",
          "Closer intersection: smaller positive $t$",
        ]}
      </F>

      {/* ── FOV ── */}
      <h2 id="fov-aspect">Field of View &amp; Aspect Ratio</h2>
      <p>
        Vertical FoV {"$\\theta$"} relates the view-plane half-height {"$h$"}{" "}
        to focal distance {"$d$"}:
      </p>
      <F>
        {[
          "$$h = d\\,\\tan\\!\\left(\\frac{\\theta}{2}\\right)$$",
          "$t=h,\\;b=-h,\\;r=h\\cdot\\text{aspect},\\;l=-r$ where $\\text{aspect}=W/H$",
        ]}
      </F>
      <p>
        Graphics engines typically fix the <em>vertical</em> FoV and let the
        horizontal extent scale with aspect ratio.
      </p>

      <h3>Y-Axis Flip</h3>
      <p>
        Screen row {"$j=0$"} is at the <em>top</em> in most raster APIs. Flip
        to match world-space {"$y$"}-up:
      </p>
      <F>{"$$v_j = 1 - \\frac{j+0.5}{H}$$"}</F>

      {/* ── RAY–SPHERE ── */}
      <h2 id="ray-sphere">Ray–Sphere Intersection</h2>
      <p>
        Substitute {"$\\mathbf{P}(t)=\\mathbf{o}+t\\mathbf{d}$"} into the unit
        sphere equation {"$x^2+y^2+z^2=1$"}:
      </p>
      <F>
        {[
          "$$\\underbrace{(d_x^2+d_y^2+d_z^2)}_{a}\\,t^2 + \\underbrace{2(o_xd_x+o_yd_y+o_zd_z)}_{b}\\,t + \\underbrace{o_x^2+o_y^2+o_z^2-1}_{c}=0$$",
          "Quadratic formula: $t=\\dfrac{-b\\pm\\sqrt{b^2-4ac}}{2a}$",
        ]}
      </F>
      <ul>
        <li>
          {"$b^2-4ac < 0$"}: miss — {"$=0$"}: tangent — {"$> 0$"}: two hits
          (take smaller positive {"$t$"})
        </li>
      </ul>

      {/* ── RAY–PLANE ── */}
      <h2 id="ray-plane">Ray–Plane Intersection</h2>
      <p>
        Plane: {"$\\mathbf{N}\\cdot\\mathbf{P}+d=0$"}. Substitute the ray:
      </p>
      <F>
        {[
          "$$t = \\frac{-(\\mathbf{N}\\cdot\\mathbf{o}+d)}{\\mathbf{N}\\cdot\\mathbf{d}}$$",
          "If $\\mathbf{N}\\cdot\\mathbf{d}=0$: ray is parallel — no intersection.",
        ]}
      </F>

      <h3>Plane from Three Non-Collinear Points</h3>
      <F>
        {[
          "$\\mathbf{N}=\\mathbf{PQ}\\times\\mathbf{PR}$ (unnormalised)",
          "$\\hat{\\mathbf{N}}=\\mathbf{PQ}\\times\\mathbf{PR}\\;/\\;\\|\\mathbf{PQ}\\times\\mathbf{PR}\\|$",
        ]}
      </F>

      {/* ── RAY–TRIANGLE ── */}
      <h2 id="ray-triangle">Ray–Triangle Intersection</h2>
      <ol>
        <li>Find the plane containing the triangle</li>
        <li>
          Ray–plane test to get intersection point {"$P$"}
        </li>
        <li>Test whether {"$P$"} lies inside the triangle</li>
      </ol>

      {/* ── POINT-IN-TRIANGLE ── */}
      <h2 id="point-in-triangle">Point-in-Triangle Test</h2>
      <p>
        The sign of the {"$z$"}-component of{" "}
        {"$\\mathbf{A}\\times\\overrightarrow{AP}$"} reveals which side of{" "}
        {"$\\mathbf{A}$"} point {"$P$"} lies on:
      </p>
      <F>
        {[
          "$z>0$: $P$ is to the left",
          "$z<0$: $P$ is to the right",
          "$z=0$: $P$ is on the line",
        ]}
      </F>
      <p>
        For triangle {"$T_0T_1T_2$"} with edges {"$\\mathbf{Q},\\mathbf{R},\\mathbf{S}$"}
        , {"$P$"} is inside iff it is on the left of all three edges:
      </p>
      <F>
        {[
          "$(\\mathbf{Q}\\times\\overrightarrow{T_0P})\\cdot\\mathbf{N}>0$",
          "$(\\mathbf{R}\\times\\overrightarrow{T_1P})\\cdot\\mathbf{N}>0$",
          "$(\\mathbf{S}\\times\\overrightarrow{T_2P})\\cdot\\mathbf{N}>0$",
        ]}
      </F>

      {/* ── AABB OVERVIEW ── */}
      <h2 id="aabb-overview">Axis-Aligned Bounding Box (AABB)</h2>
      <p>
        A box whose faces are perpendicular to the coordinate axes, defined by
        per-axis min/max pairs. Used heavily in broad-phase collision detection
        and BVH acceleration structures.
      </p>

      {/* ── AABB ALGORITHM ── */}
      <h2 id="aabb-algorithm">AABB Ray Intersection — Slab Method</h2>
      <p>
        Each axis pair of parallel planes is a <strong>slab</strong>. Compute
        the ray's entry/exit {"$t$"}-values per slab and intersect all three
        intervals:
      </p>
      <F>
        {[
          "For each axis $k\\in\\{x,y,z\\}$:",
          "$$t_{\\min,k}=\\frac{B_{k,\\min}-O_k}{D_k},\\quad t_{\\max,k}=\\frac{B_{k,\\max}-O_k}{D_k}$$",
          "Swap if $t_{\\min,k}>t_{\\max,k}$",
          "$t_\\text{near}=\\max(t_{\\min,x},t_{\\min,y},t_{\\min,z})$",
          "$t_\\text{far}=\\min(t_{\\max,x},t_{\\max,y},t_{\\max,z})$",
          "Hit iff $t_\\text{near}\\le t_\\text{far}$",
        ]}
      </F>

      <h3>Surface Normal from Hit</h3>
      <p>
        Track which axis last updated {"$t_\\text{near}$"}; negate by the sign
        of the ray direction on that axis:
      </p>
      <F>{"$$\\mathbf{N}=-\\operatorname{sign}(D_\\text{axis})\\,\\hat{\\mathbf{e}}_\\text{axis}$$"}</F>

      {/* ── AABB ROUTINES ── */}
      <h2 id="aabb-routines">AABB Utility Routines</h2>
      <pre>{`// Range helpers
rangeContains(range_min, range_max, value)
rangeOverlaps(r1_min, r1_max, r2_min, r2_max)
expandRangeByValue(range_min, range_max, value)

// AABB building
createBox(min_point, max_point)
expandBoxByPoint(aabb, point)
expandBoxByBox(aabb1, aabb2)

// AABB tests
pointInBox(aabb, point)
overlapBox(aabb1, aabb2)
rayIntersectBox(aabb, origin, direction)`}</pre>
    </div>
  );
};

export default RaytracingIntro;
